/**
 * The single source of truth for calendar dates in BrewBase.
 *
 * A `CalendarDate` is a day on the wall calendar — an expiry date, a planned
 * date, a commissioning date. It is NOT an instant. Every pg `date` column maps
 * to this type, end to end: the pg driver hands back the raw `YYYY-MM-DD`
 * string (see `pg-type-parsers.ts`), Kanel types the column as `CalendarDate`,
 * superjson ships it as a plain string, and the browser formats it directly.
 *
 * Rules (locked):
 *  - A `CalendarDate` is ALWAYS exactly `YYYY-MM-DD`. No time, no zone, no
 *    offset. It sorts and compares lexicographically.
 *  - It NEVER becomes a JS `Date` except at the date-picker edge, via
 *    `calendarDateToDate` / `calendarDateFromDate`. Those two are the only
 *    sanctioned conversions and are explicitly LOCAL-time: a picker shows a day,
 *    and a day is what the user meant.
 *  - `new Date("2026-02-14")` is UTC midnight, which is still 13 Feb in any
 *    negative-offset zone. That is the bug this type exists to make
 *    unrepresentable. Do not reintroduce it.
 *  - The brand is load-bearing. A plain `string` alias gets bypassed the first
 *    time someone interpolates; the brand makes the compiler enumerate every
 *    site. Construct values through `toCalendarDate` / `assertCalendarDate`,
 *    not through a cast.
 *
 * Arithmetic here is calendar arithmetic, done on the Y/M/D components — it is
 * never affected by DST, leap seconds, or the host timezone.
 */
import { z } from "zod";
const SHAPE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** True when `value` is a well-formed, real calendar day (`2026-02-30` is not). */
export function isCalendarDate(value) {
    if (typeof value !== "string")
        return false;
    const match = SHAPE.exec(value);
    if (!match)
        return false;
    const [, y, m, d] = match;
    const month = Number(m);
    const day = Number(d);
    if (month < 1 || month > 12 || day < 1)
        return false;
    return day <= daysInMonth(Number(y), month);
}
/**
 * Parses a `YYYY-MM-DD` string. Returns `null` for anything else — including a
 * full ISO timestamp, which is a different kind of value and must not be
 * silently truncated to a day (that truncation is exactly the timezone bug).
 */
export function toCalendarDate(value) {
    if (!value)
        return null;
    return isCalendarDate(value) ? value : null;
}
/** Throws on malformed input. Use where a bad value is a programming error. */
export function assertCalendarDate(value) {
    if (!isCalendarDate(value)) {
        throw new Error(`Not a calendar date: ${JSON.stringify(value)}`);
    }
    return value;
}
/** Zod schema for the branded type. Accepts only `YYYY-MM-DD`. */
export const calendarDate = z
    .string()
    .refine(isCalendarDate, {
    message: "Expected a calendar date in YYYY-MM-DD format",
});
/** Today, in the host's LOCAL timezone — the day the operator is living in. */
export function todayCalendarDate() {
    return calendarDateFromDate(new Date());
}
/**
 * Picker edge: takes the LOCAL year/month/day off a `Date`.
 *
 * Deliberately not `toISOString().slice(0, 10)`, which reads the UTC day and
 * shifts by one for most of the day in any non-UTC zone.
 */
export function calendarDateFromDate(date) {
    const y = String(date.getFullYear()).padStart(4, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}
/**
 * Picker edge: LOCAL midnight of the given day.
 *
 * Deliberately not `new Date(value)`, which parses `YYYY-MM-DD` as UTC midnight.
 */
export function calendarDateToDate(value) {
    const [y, m, d] = splitParts(value);
    return new Date(y, m - 1, d);
}
/** Lexicographic, which is chronological for ISO dates. Sort comparator. */
export function compareCalendarDates(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
/** Whole days from `from` to `to`; negative when `to` is earlier. */
export function differenceInCalendarDays(to, from) {
    return toEpochDay(to) - toEpochDay(from);
}
/** Calendar-day arithmetic. `n` may be negative. */
export function addCalendarDays(value, n) {
    return fromEpochDay(toEpochDay(value) + n);
}
/**
 * Calendar-month arithmetic, clamped to the end of the target month —
 * 31 Jan + 1 month is 28/29 Feb, not 2/3 Mar. `n` may be negative.
 */
export function addCalendarMonths(value, n) {
    const [y, m, d] = splitParts(value);
    const total = y * 12 + (m - 1) + n;
    const year = Math.floor(total / 12);
    const month = total - year * 12 + 1;
    const day = Math.min(d, daysInMonth(year, month));
    return join(year, month, day);
}
/** Calendar-year arithmetic. 29 Feb + 1 year clamps to 28 Feb. */
export function addCalendarYears(value, n) {
    return addCalendarMonths(value, n * 12);
}
// --- internals -------------------------------------------------------------
function splitParts(value) {
    const match = SHAPE.exec(value);
    if (!match)
        throw new Error(`Not a calendar date: ${JSON.stringify(value)}`);
    return [Number(match[1]), Number(match[2]), Number(match[3])];
}
function join(y, m, d) {
    return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
/**
 * Days since 1970-01-01. Goes through UTC purely as a counting device — no
 * local time is involved in either direction, so the host zone cannot leak in.
 */
function toEpochDay(value) {
    const [y, m, d] = splitParts(value);
    return Date.UTC(y, m - 1, d) / 86_400_000;
}
function fromEpochDay(day) {
    const date = new Date(day * 86_400_000);
    return join(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}
//# sourceMappingURL=calendar-date.js.map